/**
 * File: apps/qbench-cp/reactapp/src/shared/components/FileUpload/index.js
 *
 * Purpose: Drag-and-drop file upload component that uploads files directly to S3 using
 *     server-issued STS credentials, with per-file progress and delete confirmation.
 *
 * Scope: shared/components/FileUpload — full upload workflow (validate, request upload
 *     session, upload to S3, list, delete) for attachments tied to an entity.
 *
 * Tags: customer-portal
 *
 * Overview: Loads existing attachments for the given dataType/objectId on mount, validates
 *     dropped or browsed files against allowed image/PDF/XLSX types, then requests an
 *     attachment/asset upload session from the API and uploads each file to S3 in chunks of 5
 *     using aws-sdk with STS credentials returned by the server, tracking per-file upload
 *     progress and polling a check-upload endpoint until the server confirms completion.
 *     Renders the uploaded/uploading file list with type, name, and a remove control that
 *     opens an AlertDialog confirmation before calling the delete API.
 *
 * Dependencies: ../../utils/api (Assets, Attachments), aws-sdk, AlertDialog.
 *
 * Exports: default export `FileUpload` component.
 */

import React, { useRef, useState, useEffect } from "react";
import { NotificationManager } from "react-notifications";
import { FileType, ValidFileTypes } from "../../constants/fileUpload";
import { Assets, Attachments } from "../../utils/api";
import { chunkArray, parseAwsErr, validateFile } from "../../utils/misc";
import {
  Container,
  DropContainer,
  DropMessage,
  FileStatusBar,
  FileTypeDiv,
  FileName,
  FileRemove,
  FileDisplayContainer,
} from "./Styles";
import AWS from "aws-sdk";
import AlertDialog from "../AlertDialog";

const FileUpload = (props) => {
  const fileInputRef = useRef();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [attachmentToDelete, setAttachmentToDelete] = useState();
  const listAttachments = props.listAttachments ?? true;

  let remainingUpload = 0;

  useEffect(() => {
    let isMounted = true;
    const _invoke = async () => {
      if (!props.objectId) return;
      try {
        let res = await Attachments.get(props.dataType, {
          ids: [props.objectId],
        });
        if (res && res.data && isMounted) {
			setUploadedFiles(res.data);
		}
      } catch (err) {
        console.error(err);
      }
    };
    _invoke();

    return () => {
		isMounted = false;
	};

  }, [props.dataType, props.objectId]);

  const preventDefault = (e) => {
    e.preventDefault();
  };

  const dragOver = (e) => {
    preventDefault(e);
  };

  const dragEnter = (e) => {
    preventDefault(e);
  };

  const dragLeave = (e) => {
    preventDefault(e);
  };

  const fileDrop = (e) => {
    preventDefault(e);
    const files = e.dataTransfer.files;
    if (files.length) {
      handleFiles(files);
    }
  };

  const filesSelected = () => {
    if (fileInputRef.current.files.length) {
      handleFiles(fileInputRef.current.files);
    }
  };

  const fileInputClicked = () => {
    fileInputRef.current.click();
  };

  const handleFiles = (files) => {
    let filesToAdd = [];
    let existingFiles = uploadedFiles;
    for (let i = 0; i < files.length; i++) {
      let file = files[i];

      if (!validateFile(file, [FileType.IMAGE, FileType.PDF, FileType.XLSX])) {
        NotificationManager.error(
          `${file.name} is not an image or PDF file!`,
          "Invalid File Type",
          3000
        );
        return;
      } else {
        existingFiles = existingFiles.filter(
          (f) => f.asset.file_name !== file.name
        );
        filesToAdd.push(file);
      }
    }
    uploadFiles(filesToAdd, existingFiles);
  };

  const uploadToS3 = (
    s3,
    _files,
    { Body, Bucket, Key, ServerSideEncryption, assetId }
  ) => {
    let retryCount = 0;

    const checkUpload = async () => {
      try {
        let response = await Assets.checkUpload(assetId);
        let uploaded = response.data;

        if (uploaded) {
          NotificationManager.success("Upload Completed");
          return;
        } else {
          retryCount += 1;
          if (retryCount > 10) {
            NotificationManager.error("Upload Session Timeout");
            return;
          }
          setTimeout(checkUpload, 1000);
        }
      } catch (err) {
        console.error(err);
      }
    };

    s3.upload({ Body, Bucket, Key, ServerSideEncryption }, (err, data) => {
      if (err) {
        let [errShort, errLong] = parseAwsErr(err);
        NotificationManager.error(errShort, "Unable to upload file.", 3000);
        console.error(errLong);
        //   button.button("reset");
      } else {
        let fileNum = _files.length - remainingUpload + 1;
        NotificationManager.success(
          `File ${fileNum} of ${_files.length} Upload Completed`,
          "Success!",
          3000
        );

        remainingUpload--;

        if (remainingUpload === 0) {
          checkUpload(assetId);
        }
      }
    }).on("httpUploadProgress", (progress) => {
      let percentage = Math.floor((progress.loaded / progress.total) * 100);
      setUploadedFiles((prev) =>
        prev.map((f) => {
          if (f.asset.file_name === Body.name) {
            f.percentUploaded = percentage;
          }
          return f;
        })
      );
    });
  };

  const uploadFiles = async (files, existingFiles) => {
    let chunks = chunkArray(files, 5); // Only upload 5 at a time
    remainingUpload = files.length;
    let uploadedFilesArray = []

    for (let i = 0; i < chunks.length; i++) {
      let attachments = {
        file_names: [],
        object_id: props.objectId || null,
        published_to_portal: true,
        attach_to_report: false,
      };

      for (let ii = 0; ii < chunks[i].length; ii++) {
        let file = chunks[i][ii];
        attachments.file_names.push(file.name);
      }

      try {
        let uploadData = await Attachments.save(props.dataType, attachments);
        uploadData = uploadData.data;
        let fileArr = [];
        for (let iii = 0; iii < uploadData.attachments.length; iii++) {
          uploadData.attachments[iii].percentUploaded = 10;
          fileArr.push(uploadData.attachments[iii]);
        }
        uploadedFilesArray.push(...fileArr)

        if (props.setNewAttachments) {
          props.setNewAttachments((prev) => [
            ...prev,
            ...uploadData.attachments,
          ]);
        }

        let s3Bucket = uploadData.s3_bucket;
        let stsCreds = uploadData.sts_credentials_object;

        AWS.config.credentials = new AWS.Credentials(
          stsCreds["AccessKeyId"],
          stsCreds["SecretAccessKey"],
          stsCreds["SessionToken"]
        );
        let s3 = new AWS.S3();

        for (let ii = 0; ii < chunks[i].length; ii++) {
          let file = chunks[i][ii];
          let assetKey = uploadData.asset_keys[ii];
          let assetId = uploadData.asset_ids[ii];

          let params = {
            Body: file,
            Bucket: s3Bucket,
            Key: assetKey,
            ServerSideEncryption: "AES256",
            assetId: assetId,
          };

          if (params.Key.endsWith(".pdf")) {
            params.ContentType = "application/pdf";
          }

          uploadToS3(s3, files, params);
        }
      } catch (err) {
        console.error(err);
      }
    }
    setUploadedFiles([...uploadedFilesArray, ...existingFiles]);
  };

  const fileType = (fileName) => {
    if (fileName) {
      fileName = fileName.substring(
        fileName.lastIndexOf(".") + 1,
        fileName.length
      );
    }
    return fileName;
  };
  const RenderFileStatusBar = ({ attachment }) => {
    return (
      <FileStatusBar
        onClick={() => downloadAttachment(attachment)}
        percentUploaded={attachment.percentUploaded || 0}
      >
        <div className="progress"></div>
        <div>
          <div className="qb-file-upload-row-left">
            <FileTypeDiv>{fileType(attachment.asset.file_name)}</FileTypeDiv>
          </div>
          <div className="qb-file-upload-row-middle">
            <FileName onClick={() => null}>
              {attachment.asset.file_name}
            </FileName>
          </div>
          <div className="qb-file-upload-row-right">
            <FileRemove onClick={(e) => deleteAttachment(e, attachment)}>
              X
            </FileRemove>
          </div>
        </div>
      </FileStatusBar>
    );
  };

  const downloadAttachment = async (attachment) => {
    if (attachment.percentUploaded && attachment.percentUploaded < 100) {
      alert("File is still uploading!");
    }
    try {
      const win = window.open();
      const {
        data: { url },
      } = await Assets.download(attachment.asset_id);
      win.location.href = url;
    } catch (err) {
      console.error(err);
    }
  };

  const deleteAttachment = (e, attachment) => {
    e.stopPropagation();
    setAttachmentToDelete(attachment);
  };

  const deleteAttachmentConfirm = async () => {
    await Attachments.delete(props.dataType, attachmentToDelete.id);
    setUploadedFiles((prev) =>
      prev.filter((a) => a.id !== attachmentToDelete.id)
    );
    var deletingElement = document.getElementsByClassName('uploadInput');
    deletingElement[0].value = '';
    setAttachmentToDelete(null);
  };

  const getAcceptedFileTypes = () => {
    const allTypes = new Set();
    Object.values(ValidFileTypes).forEach(types => {
        types.forEach(type => {
          allTypes.add(type);
        });
    });
    return Array.from(allTypes).join(', ');
  };

  return (
		<>
			<Container>
				<DropContainer
					onDragOver={dragOver}
					onDragEnter={dragEnter}
					onDragLeave={dragLeave}
					onDrop={fileDrop}
					onClick={fileInputClicked}
					className='dropContainer'
				>
					<DropMessage>
						{props.dropMessage ? props.dropMessage : 'ADD FILES'}
					</DropMessage>
					<input
            className="uploadInput"
						ref={fileInputRef}
						style={{ display: 'none' }}
						type='file'
						accept={getAcceptedFileTypes()}
						multiple
						onChange={filesSelected}
					/>
				</DropContainer>
				{listAttachments && (<FileDisplayContainer>
					{uploadedFiles.map((data, i) => (
						<RenderFileStatusBar attachment={data} key={i} />
					))}
				</FileDisplayContainer>)}
			</Container>
			<AlertDialog
				show={attachmentToDelete ? true : false}
				handleYes={deleteAttachmentConfirm}
				handleNo={() => setAttachmentToDelete(null)}
			>
				<p
					style={{
						textAlign: 'center',
						fontWeight: 800,
						fontSize: '2em',
            overflowWrap: "anywhere"
					}}
				>
					Are you sure you want to delete{' '}
					{attachmentToDelete
						? attachmentToDelete.asset.file_name
						: 'this Attachment'}
					?
				</p>
			</AlertDialog>
		</>
  );
};

export default FileUpload;
